import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { PreApplicationDocType } from '../../../../../core/applications/pre-application.types';
import { IconComponent } from '../../../../../shared/icon/icon';
import { DocUpload } from '../../../shared/doc-drop/doc-drop';
import { Step } from '../intake-checklist';

/**
 * The synthetic "nothing shared" Financial Data row. Not a document bucket: it
 * stands in for BOTH financial buckets when neither holds a document (master
 * document's no-financials review), and jumps back to Step 2.
 */
const FINANCIAL_DATA_ROW = 'FINANCIAL_DATA';

/** One recap row on the Application Summary step. */
interface RecapRow {
  /** Which bucket the row reports on (or the synthetic no-financials row). */
  docType: PreApplicationDocType | typeof FINANCIAL_DATA_ROW;
  label: string;
  /** ok = uploaded & checks passed; checking = verdicts pending; attention = amber/red verdicts; missing = nothing yet. */
  tone: 'ok' | 'checking' | 'attention' | 'missing';
  /** The one-line status ("3 bank statements uploaded and checked"). */
  text: string;
  /** Step to jump back to when something's missing or worth a look. */
  step: Extract<Step, 'bank' | 'financials'>;
  /** Label for the jump-back action, or null when there's nothing to do. */
  actionLabel: string | null;
  /** File names behind the row (recap chips). */
  files: string[];
}

/**
 * Step 3 of the intake: a live recap of what was shared, what happens next, the
 * Debtors Finance unlock and the submit tracks. The act of submitting stays with
 * the shell; this renders the state of it and says which track was pressed.
 */
@Component({
  selector: 'app-summary-step',
  imports: [MatButtonModule, MatProgressSpinnerModule, IconComponent],
  templateUrl: './summary-step.html',
  styleUrl: './summary-step.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SummaryStepComponent {
  /** Every upload on the intake, across the buckets. */
  readonly uploads = input.required<DocUpload<PreApplicationDocType>[]>();
  readonly hasFinancialDocs = input.required<boolean>();
  readonly dfUnlocked = input.required<boolean>();
  readonly reopened = input.required<boolean>();
  readonly canSubmit = input.required<boolean>();
  readonly isSubmitting = input.required<boolean>();
  readonly submittingTrack = input.required<'df' | 'ks' | null>();
  readonly anyInFlight = input.required<boolean>();

  readonly back = output<void>();
  readonly jumpTo = output<Step>();
  /** Which track was pressed: `true` records the Debtors Finance interest. */
  readonly submitTrack = output<boolean>();

  /** How many bank statements have been added (done or uploading). */
  protected readonly bankStatementCount = computed(
    () => this.uploads().filter((u) => u.docType === 'BANK_STATEMENT').length,
  );

  /** Server-confirmed uploads for one bucket (in-flight ones don't count yet). */
  private docsIn(docType: PreApplicationDocType): DocUpload<PreApplicationDocType>[] {
    return this.uploads().filter((u) => u.docType === docType && u.doc !== null);
  }

  /** Build one recap row from a bucket's uploads + their AI-check verdicts. */
  private recapRow(
    docType: PreApplicationDocType,
    label: string,
    step: Extract<Step, 'bank' | 'financials'>,
    text: {
      ok: (n: number) => string;
      missing: string;
    },
    missingAction: string | null,
  ): RecapRow {
    const docs = this.docsIn(docType);
    const files = docs.map((u) => u.fileName);
    if (docs.length === 0) {
      return {
        docType,
        label,
        tone: 'missing',
        text: text.missing,
        step,
        actionLabel: missingAction,
        files,
      };
    }
    const pending = docs.filter((u) => u.doc?.assess_status === 'PENDING').length;
    const attention = docs.filter(
      (u) => u.doc?.assess_status === 'WARNING' || u.doc?.assess_status === 'PROBLEM',
    ).length;
    if (attention > 0) {
      return {
        docType,
        label,
        tone: 'attention',
        text: `${text.ok(docs.length)} — ${attention} worth a look before you submit`,
        step,
        actionLabel: 'Review',
        files,
      };
    }
    if (pending > 0) {
      return {
        docType,
        label,
        tone: 'checking',
        text: `${text.ok(docs.length)} — quality checks still running`,
        step,
        actionLabel: null,
        files,
      };
    }
    return {
      docType,
      label,
      tone: 'ok',
      text: `${text.ok(docs.length)} and checked`,
      step,
      actionLabel: null,
      files,
    };
  }

  /** The "Bank Data" recap row. */
  protected readonly bankRecap = computed<RecapRow>(() =>
    this.recapRow(
      'BANK_STATEMENT',
      'Bank statements',
      'bank',
      {
        ok: (n) => `${n} bank ${n === 1 ? 'statement' : 'statements'} uploaded`,
        missing: 'No bank statements yet — they give us the most to work with',
      },
      'Add bank statements',
    ),
  );

  /**
   * The "Financial data" recap rows. Per bucket (age analysis, financial
   * statements) when at least one holds a document; ONE row saying nothing was
   * provided when neither does — the master document's no-financials review,
   * which states the consequence (assessed on bank transaction analysis)
   * rather than listing two empty buckets.
   */
  protected readonly financialRecap = computed<RecapRow[]>(() => {
    if (!this.hasFinancialDocs()) {
      return [
        {
          docType: FINANCIAL_DATA_ROW,
          label: 'Financial data',
          tone: 'missing',
          text: 'Not provided — your application will be assessed based on bank transaction analysis',
          step: 'financials',
          actionLabel: 'Add financial information',
          files: [],
        },
      ];
    }
    return this.financialRecapPerBucket();
  });

  /** The two per-bucket Financial Data rows (age analysis + financial statements). */
  private readonly financialRecapPerBucket = computed<RecapRow[]>(() => [
    this.recapRow(
      'AGE_ANALYSIS',
      'Age analysis',
      'financials',
      {
        ok: (n) => `${n} ${n === 1 ? 'file' : 'files'} provided`,
        missing: 'Not added — sharing it unlocks Debtors Finance',
      },
      'Add age analysis',
    ),
    this.recapRow(
      'FINANCIALS',
      'Financial statements',
      'financials',
      {
        ok: (n) => `${n} ${n === 1 ? 'file' : 'files'} provided`,
        missing: 'Not added — optional, but can improve your offer',
      },
      'Add financials',
    ),
  ]);

  /** The recap grouped for rendering: Bank Data, then Financial Data. */
  protected readonly recapGroups = computed<{ title: string; rows: RecapRow[] }[]>(() => [
    { title: 'Bank Data', rows: [this.bankRecap()] },
    { title: 'Financial Data', rows: this.financialRecap() },
  ]);

  /** The summary step's lead line reflects what was actually shared. */
  protected readonly summaryLead = computed(() => {
    const bank = this.docsIn('BANK_STATEMENT').length > 0;
    const fin = this.docsIn('AGE_ANALYSIS').length > 0 || this.docsIn('FINANCIALS').length > 0;
    if (bank && fin) return 'Based on your bank statements and financial data.';
    if (bank) return 'Based on your bank statements.';
    if (fin) return 'Based on your financial data.';
    return "Here's what you're sharing — you can still go back and add documents.";
  });
}
